/**
 * The Strategy page — ROADMAP 5.19.
 *
 * Every strategy this trader owns, as cards (see `StrategyCard` for why a
 * card and not a row), grouped by bias. Their own copies (5.4), seeded at
 * registration (5.18), so the page is never empty on a first visit.
 *
 * There is no "New strategy" button and no delete. The scope is adjusting
 * the defaults a trader was given; creating an arbitrary structure is Phase
 * 7.2. The absence of delete is also load-bearing further down: it is what
 * lets the seeding backfill treat a missing row as "never seeded" rather than
 * "deliberately removed".
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Plus } from 'lucide-react'
import { api } from '@/lib/api'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { StrategyEditor } from './components/StrategyEditor'
import { StrategyCard } from './components/StrategyCard'
import { StrategyToolbar } from './components/StrategyToolbar'
import { BIAS_CLASS, BIAS_LABEL, BIAS_ORDER } from './constants'
import {
  ALL_BIASES,
  biasCounts,
  EMPTY_QUERY,
  filterStrategies,
  type StrategyQuery,
} from './utils/strategyFilter'
import type { StrategyCreate, StrategyDto, StrategyPatch } from '@/types/strategy'

export default function Strategies() {
  const [items, setItems] = useState<StrategyDto[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  /*
   * `null` is closed and `'NEW'` is the builder with nothing in it. A
   * nullable `StrategyDto` alone cannot say both: the editor needs null to
   * mean "creating", so the page needs a third state to mean "not open".
   */
  const [editing, setEditing] = useState<StrategyDto | 'NEW' | null>(null)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [filter, setFilter] = useState<StrategyQuery>(EMPTY_QUERY)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const r = await api.get<{ items: StrategyDto[] }>('/strategies')
      setItems(r.items)
      setError(null)
    } catch (e: unknown) {
      setError(
        e instanceof Error ? e.message : 'Could not load your strategies.',
      )
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  async function save(patch: StrategyPatch) {
    if (editing === null || editing === 'NEW') return
    setSaving(true)
    try {
      const r = await api.patch<{ strategy: StrategyDto }>(
        `/strategies/${editing.id}`,
        patch,
      )
      // Replace in place rather than refetching the list: the row is the
      // server's own answer, so the screen cannot drift from what was saved.
      setItems((xs) => xs.map((s) => (s.id === r.strategy.id ? r.strategy : s)))
      setEditing(null)
      setError(null)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Could not save that change.')
    } finally {
      setSaving(false)
    }
  }

  async function create(spec: StrategyCreate) {
    setSaving(true)
    try {
      const r = await api.post<{ strategy: StrategyDto }>('/strategies', spec)
      /*
       * The SERVER's row goes into the list, not the body we sent. They
       * differ in id, leg ids, seq and the derived construction line, and
       * reconciling that in the browser is how a list drifts from the
       * database it is meant to be showing.
       */
      setItems((xs) => [...xs, r.strategy])
      setEditing(null)
      setError(null)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Could not create that strategy.')
    } finally {
      setSaving(false)
    }
  }

  async function remove(strategy: StrategyDto) {
    setDeleting(true)
    try {
      await api.del(`/strategies/${strategy.id}`)
      setItems((xs) => xs.filter((s) => s.id !== strategy.id))
      setEditing(null)
      setError(null)
    } catch (e: unknown) {
      // A seeded strategy refuses here, in words — the seeder would bring it
      // straight back, so the button must not appear to have worked.
      setError(e instanceof Error ? e.message : 'Could not delete that strategy.')
    } finally {
      setDeleting(false)
    }
  }

  const visible = useMemo(
    () => filterStrategies(items, filter),
    [items, filter],
  )
  const counts = useMemo(
    () => biasCounts(items, filter.query),
    [items, filter.query],
  )
  /** Pills are offered for what the ACCOUNT holds, not for what survived the search. */
  const biases = useMemo(
    () => BIAS_ORDER.filter((b) => items.some((s) => s.bias === b)),
    [items],
  )

  // One bias selected means the heading would only repeat the pill above it.
  const grouped = filter.bias === ALL_BIASES

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <header className="flex flex-wrap items-center gap-x-4 gap-y-2 px-6 pb-3 pt-5">
        <div className="mr-auto">
          <h1 className="text-[17px] font-semibold">Strategies</h1>
          <p className="text-[12.5px] text-muted-foreground">
            Build strategy and keep every rule organized in one place.
          </p>
        </div>
        {!loading && items.length > 0 && (
          <StrategyToolbar
            query={filter.query}
            bias={filter.bias}
            biases={biases}
            counts={counts}
            onQuery={(query) => setFilter((f) => ({ ...f, query }))}
            onBias={(bias) => setFilter((f) => ({ ...f, bias }))}
          />
        )}
        {!loading && (
          <Button
            type="button"
            size="sm"
            onClick={() => setEditing('NEW')}
            data-testid="new-strategy"
          >
            <Plus size={13} />
            New strategy
          </Button>
        )}
      </header>

      {error && (
        <div
          className="mx-6 mb-3 rounded-[10px] border border-destructive/40 bg-destructive/10 px-3 py-2 text-[12.5px] text-destructive"
          data-testid="strategies-error"
        >
          {error}
        </div>
      )}

      <div className="flex-1 overflow-y-auto px-6 pb-8">
        {loading && (
          <p className="py-10 text-center text-[13px] text-muted-foreground">
            Loading…
          </p>
        )}

        {!loading && items.length === 0 && (
          /*
           * Reachable only if seeding failed at registration. It says what to
           * do rather than pretending the trader has no strategies.
           */
          <p
            className="py-10 text-center text-[13px] text-muted-foreground"
            data-testid="strategies-empty"
          >
            No strategies on this account yet. They are created at registration
            — if this persists, ask an administrator to run the strategy
            backfill.
          </p>
        )}

        {!loading && items.length > 0 && visible.length === 0 && (
          <p
            className="py-10 text-center text-[13px] text-muted-foreground"
            data-testid="strategies-no-match"
          >
            No strategy matches that.
          </p>
        )}

        {BIAS_ORDER.map((bias) => {
          const group = visible.filter((s) => s.bias === bias)
          if (group.length === 0) return null
          return (
            <section key={bias} className="mb-5" data-testid={`bias-${bias}`}>
              {grouped && (
                <h2
                  className={cn(
                    'mb-2 text-[11px] font-semibold uppercase tracking-wider',
                    BIAS_CLASS[bias],
                  )}
                >
                  {BIAS_LABEL[bias] ?? bias}
                </h2>
              )}
              <div className="grid gap-3 [grid-template-columns:repeat(auto-fill,minmax(304px,1fr))]">
                {group.map((s) => (
                  <StrategyCard
                    key={s.id}
                    strategy={s}
                    onEdit={() => setEditing(s)}
                  />
                ))}
              </div>
            </section>
          )
        })}
      </div>

      {editing !== null && (
        <StrategyEditor
          /*
           * KEYED, so switching from one strategy to another remounts the
           * form. Without it the drafts are initial state React keeps, and
           * opening a second strategy would show the first one's legs.
           */
          key={editing === 'NEW' ? 'new' : editing.id}
          strategy={editing === 'NEW' ? null : editing}
          saving={saving}
          deleting={deleting}
          onSave={(p) => void save(p)}
          onCreate={(spec) => void create(spec)}
          onDelete={editing === 'NEW' ? undefined : () => void remove(editing)}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  )
}
